import { z } from 'zod'
import { FOLDER_ACK_TEXT_MAX } from './consent.ts'
import { GroupRefSchema } from './groups.ts'
import { LocaleSchema } from './locale.ts'

/**
 * Connected folders (PRD-25): a knowledge source kept current by a folder somewhere else.
 *
 * The vocabulary is the product's, not the providers'. A person connects an *account*, chooses
 * a *folder*, sees what will be *loaded*, and afterwards the source is *watching* it. Nothing
 * here says delta, cursor, token or OAuth; those live on the server, and the words a maintainer
 * reads come from `apps/web/src/i18n/messages/<locale>/knowledgeFolders.ts`.
 */

// ---------------------------------------------------------------------------
// Providers and accounts
// ---------------------------------------------------------------------------

/**
 * `microsoft` serves SharePoint and OneDrive: both are drives in Graph, one token reaches
 * both, and one expired sign-in must surface as one broken account (D-2). Which surface a
 * folder was browsed through is display-only and lives on the folder.
 */
export const ConnectorProviderSchema = z.enum(['microsoft', 'google', 's3'])
export type ConnectorProvider = z.infer<typeof ConnectorProviderSchema>

export const ConnectorSurfaceSchema = z.enum(['sharepoint', 'onedrive', 'drive', 's3'])
export type ConnectorSurface = z.infer<typeof ConnectorSurfaceSchema>

export const SURFACE_PROVIDER: Record<ConnectorSurface, ConnectorProvider> = {
  sharepoint: 'microsoft',
  onedrive: 'microsoft',
  drive: 'google',
  s3: 's3'
}

/**
 * Whose eyes the platform borrows (D-3).
 *
 *  - `delegated`: a person signed in; the platform reads what they can read, and the folder
 *    stops when their account is deactivated.
 *  - `application`: the deployment's own identity — Microsoft app-only with admin consent, or a
 *    Google service account the folder is shared with — which outlives any employee.
 *  - `access_key`: S3.
 */
export const ConnectorModeSchema = z.enum(['delegated', 'application', 'access_key'])
export type ConnectorMode = z.infer<typeof ConnectorModeSchema>

/** Four states, each with one action (D-13). */
export const ConnectionHealthSchema = z.enum(['healthy', 'rate_limited', 'needs_reauth', 'error'])
export type ConnectionHealth = z.infer<typeof ConnectionHealthSchema>

/** A structured provider failure: never a raw body. */
export const ConnectorProblemSchema = z.object({
  code: z.string(),
  message: z.string(),
  at: z.iso.datetime()
})
export type ConnectorProblem = z.infer<typeof ConnectorProblemSchema>

export const ConnectorConnectionSchema = z.object({
  id: z.uuid(),
  provider: ConnectorProviderSchema,
  mode: ConnectorModeSchema,
  /** `ana@acme.com`, `Acme (application)`, `arn:aws:iam::123:user/reader`. */
  accountLabel: z.string(),
  ownerId: z.uuid(),
  ownerName: z.string(),
  /** True when the caller owns it: only the owner (or an administrator) may reconnect or remove it. */
  mine: z.boolean(),
  health: ConnectionHealthSchema,
  healthDetail: ConnectorProblemSchema.nullable(),
  /** While rate-limited: when the provider may be asked again. */
  retryAfter: z.iso.datetime().nullable(),
  /** How many folders use it, so removing it can state its consequence. */
  folderCount: z.number().int().nonnegative(),
  lastVerifiedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime()
})
export type ConnectorConnection = z.infer<typeof ConnectorConnectionSchema>

/** What this deployment can connect to, so the flow offers only what an operator set up. */
export const ConnectorAvailabilitySchema = z.object({
  microsoft: z.object({ configured: z.boolean(), tenantId: z.string() }),
  google: z.object({ configured: z.boolean() }),
  s3: z.object({ configured: z.literal(true), endpointHosts: z.array(z.string()) }),
  /** The address each provider's console must be told to send the browser back to. */
  redirectUri: z.string(),
  /** The per-document ceiling, for the copy that explains "too large". */
  uploadMaxBytes: z.number().int().positive(),
  maxItemsPerFolder: z.number().int().positive()
})
export type ConnectorAvailability = z.infer<typeof ConnectorAvailabilitySchema>

// ---------------------------------------------------------------------------
// Roots
// ---------------------------------------------------------------------------

export const ConnectorRootSchema = z.discriminatedUnion('surface', [
  z.object({
    surface: z.literal('sharepoint'),
    siteId: z.string().min(1),
    siteName: z.string(),
    driveId: z.string().min(1),
    driveName: z.string(),
    /** `root` for the library itself. */
    itemId: z.string().min(1)
  }),
  z.object({ surface: z.literal('onedrive'), driveId: z.string().min(1), itemId: z.string().min(1) }),
  z.object({
    surface: z.literal('drive'),
    /** The folder's id, or the shared drive's id for its root. */
    fileId: z.string().min(1),
    /** Set inside a shared drive; the change feed is per drive. */
    driveId: z.string().nullable(),
    driveName: z.string().nullable()
  }),
  z.object({
    surface: z.literal('s3'),
    bucket: z.string().min(3).max(63),
    /** Empty for the whole bucket, otherwise ending in `/`. */
    prefix: z.string().max(1024),
    region: z.string().min(1)
  })
])
export type ConnectorRoot = z.infer<typeof ConnectorRootSchema>

// ---------------------------------------------------------------------------
// Folders
// ---------------------------------------------------------------------------

export const FolderStatusSchema = z.enum(['previewing', 'active', 'paused', 'needs_reauth', 'error'])
export type FolderStatus = z.infer<typeof FolderStatusSchema>

export const SelectionModeSchema = z.enum(['all', 'selected'])
export type SelectionMode = z.infer<typeof SelectionModeSchema>

export const SelectionFiltersSchema = z.object({
  /** Lower-case extensions with the dot, or null for every readable format. */
  extensions: z.array(z.string()).nullable(),
  maxBytes: z.number().int().positive().nullable(),
  modifiedAfter: z.iso.datetime().nullable()
})
export type SelectionFilters = z.infer<typeof SelectionFiltersSchema>

/** What the preview and the folder row count. Every number is post-selection unless it says otherwise. */
export const FolderStatsSchema = z.object({
  /** Everything listed, files and folders, before any decision. */
  listed: z.number().int().nonnegative(),
  files: z.number().int().nonnegative(),
  folders: z.number().int().nonnegative(),
  bytes: z.number().int().nonnegative(),
  /** Files the selection includes and the platform can read. */
  included: z.number().int().nonnegative(),
  includedBytes: z.number().int().nonnegative(),
  /** Of the included: what has a document today, what is waiting, what failed. */
  synced: z.number().int().nonnegative(),
  queued: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  /** Excluded, by why. */
  unsupported: z.number().int().nonnegative(),
  tooLarge: z.number().int().nonnegative(),
  filtered: z.number().int().nonnegative(),
  leftOut: z.number().int().nonnegative(),
  /** Files with no known size (Google-native files before export). */
  unknownSize: z.number().int().nonnegative()
})
export type FolderStats = z.infer<typeof FolderStatsSchema>

export const SyncKindSchema = z.enum(['preview', 'walk', 'delta'])
export type SyncKind = z.infer<typeof SyncKindSchema>

export const SyncStatusSchema = z.enum(['running', 'succeeded', 'failed'])

export const SyncChangeSchema = z.object({
  remoteId: z.string(),
  name: z.string(),
  path: z.string(),
  change: z.enum(['added', 'changed', 'removed', 'withdrawn', 'moved', 'renamed'])
})
export type SyncChange = z.infer<typeof SyncChangeSchema>

export const ConnectorSyncSchema = z.object({
  id: z.uuid(),
  folderId: z.uuid(),
  kind: SyncKindSchema,
  status: SyncStatusSchema,
  startedAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().nullable(),
  listed: z.number().int().nonnegative(),
  added: z.number().int().nonnegative(),
  changed: z.number().int().nonnegative(),
  removed: z.number().int().nonnegative(),
  withdrawn: z.number().int().nonnegative(),
  queued: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  resync: z.boolean(),
  error: ConnectorProblemSchema.nullable(),
  changes: z.array(SyncChangeSchema),
  /** `schedule`, or the display name of the person who pressed Check now. */
  triggeredBy: z.string()
})
export type ConnectorSync = z.infer<typeof ConnectorSyncSchema>

export const ConnectorFolderSchema = z.object({
  id: z.uuid(),
  sourceId: z.uuid(),
  sourceName: z.string(),
  connection: z.object({
    id: z.uuid(),
    provider: ConnectorProviderSchema,
    mode: ConnectorModeSchema,
    accountLabel: z.string(),
    health: ConnectionHealthSchema,
    healthDetail: ConnectorProblemSchema.nullable(),
    retryAfter: z.iso.datetime().nullable(),
    ownerName: z.string(),
    /** Whose sign-in it is: only they, or an administrator, can renew a delegated account. */
    ownerId: z.uuid()
  }),
  surface: ConnectorSurfaceSchema,
  root: ConnectorRootSchema,
  displayPath: z.string(),
  webUrl: z.string().nullable(),
  status: FolderStatusSchema,
  /** True while a run holds the folder. */
  syncing: z.boolean(),
  selectionMode: SelectionModeSchema,
  filters: SelectionFiltersSchema,
  /** The listing stopped at the cap; what was found is kept. */
  truncated: z.boolean(),
  intervalMs: z.number().int().positive(),
  nextSyncAt: z.iso.datetime().nullable(),
  lastSyncAt: z.iso.datetime().nullable(),
  lastSuccessAt: z.iso.datetime().nullable(),
  lastError: ConnectorProblemSchema.nullable(),
  stats: FolderStatsSchema,
  audience: z.object({
    acknowledged: z.boolean(),
    /** Who could use the base when the folder's reach was acknowledged (PRD-26 D-38). */
    access: z.enum(['private', 'groups', 'everyone']).nullable(),
    groups: z.array(GroupRefSchema),
    /** The base's reach widened since — access, a move, or an agent allowed to answer from it. */
    stale: z.boolean()
  }),
  /** Rules that name items no longer in the folder: shown as a count, never honoured silently. */
  orphanRules: z.number().int().nonnegative(),
  lastSync: ConnectorSyncSchema.nullable(),
  createdByName: z.string(),
  createdAt: z.iso.datetime()
})
export type ConnectorFolder = z.infer<typeof ConnectorFolderSchema>

// ---------------------------------------------------------------------------
// Items: the selection table, the left-out list, the not-loaded list
// ---------------------------------------------------------------------------

export const ItemDecisionSchema = z.enum(['included', 'excluded'])
export const ItemStateSchema = z.enum(['discovered', 'queued', 'fetching', 'synced', 'failed', 'withdrawn', 'removed'])
export type ItemState = z.infer<typeof ItemStateSchema>

/** Why an item is excluded, as a code the client turns into words. */
export const ExclusionReasonSchema = z.enum([
  'rule',
  'not_selected',
  'unsupported',
  'too_large',
  'too_old',
  'type',
  'removed_by_steward',
  'folder',
  // PRD-26 D-41: the knowledge base holds as much as its owner's limits allow.
  'base_full'
])
export type ExclusionReason = z.infer<typeof ExclusionReasonSchema>

export const ConnectorItemSchema = z.object({
  id: z.uuid(),
  remoteId: z.string(),
  parentRemoteId: z.string().nullable(),
  kind: z.enum(['file', 'folder']),
  name: z.string(),
  path: z.string(),
  size: z.number().int().nonnegative().nullable(),
  mimeType: z.string().nullable(),
  modifiedAt: z.iso.datetime().nullable(),
  webUrl: z.string().nullable(),
  decision: ItemDecisionSchema,
  reason: ExclusionReasonSchema.nullable(),
  /** For an unsupported file: what it is, in words. */
  detail: z.string().nullable(),
  state: ItemStateSchema,
  documentId: z.uuid().nullable(),
  error: z.string().nullable(),
  /** The explicit rule on this node, if any. */
  rule: z.enum(['include', 'exclude']).nullable(),
  /** Folder rows: post-filter counts beneath. */
  subtree: z.object({
    files: z.number().int().nonnegative(),
    included: z.number().int().nonnegative(),
    bytes: z.number().int().nonnegative(),
    includedBytes: z.number().int().nonnegative()
  }),
  /** Folder rows: the tri-state the checkbox shows. */
  effective: z.enum(['all', 'none', 'some']).nullable(),
  removedAt: z.iso.datetime().nullable()
})
export type ConnectorItem = z.infer<typeof ConnectorItemSchema>

export const ConnectorItemPageSchema = z.object({
  items: z.array(ConnectorItemSchema),
  total: z.number().int().nonnegative(),
  nextOffset: z.number().int().nonnegative().nullable(),
  breadcrumb: z.array(z.object({ remoteId: z.string(), name: z.string() }))
})
export type ConnectorItemPage = z.infer<typeof ConnectorItemPageSchema>

// ---------------------------------------------------------------------------
// The preview summary
// ---------------------------------------------------------------------------

export const PreviewSummarySchema = z.object({
  stats: FolderStatsSchema,
  /** True while the listing is still running. */
  counting: z.boolean(),
  truncated: z.boolean(),
  byExtension: z.array(z.object({ extension: z.string(), count: z.number().int(), bytes: z.number().int() })),
  unsupportedByKind: z.array(z.object({ label: z.string(), count: z.number().int() })),
  /** The largest file left out for size, so the copy can say "the largest here is 180 MB". */
  largestBytes: z.number().int().nonnegative().nullable(),
  /** One proposal from the data, or none. */
  suggestion: z
    .object({
      kind: z.literal('modified_after'),
      /** Files last changed before this instant. */
      before: z.iso.datetime(),
      count: z.number().int().positive()
    })
    .nullable()
})
export type PreviewSummary = z.infer<typeof PreviewSummarySchema>

// ---------------------------------------------------------------------------
// Browsing a provider before a folder exists
// ---------------------------------------------------------------------------

export const BrowseEntrySchema = z.object({
  remoteId: z.string(),
  name: z.string(),
  kind: z.enum(['file', 'folder', 'site', 'drive']),
  /** Known cheaply on Graph; null elsewhere — and an absent number beats a wrong one. */
  childCount: z.number().int().nonnegative().nullable(),
  size: z.number().int().nonnegative().nullable(),
  webUrl: z.string().nullable(),
  modifiedAt: z.iso.datetime().nullable()
})
export type BrowseEntry = z.infer<typeof BrowseEntrySchema>

export const BrowsePageSchema = z.object({
  entries: z.array(BrowseEntrySchema),
  next: z.string().nullable(),
  breadcrumb: z.array(z.object({ remoteId: z.string(), name: z.string() })),
  /** How many files sit at this level beside the folders, when the provider says. */
  filesHere: z.number().int().nonnegative().nullable(),
  /** The root the flow would create if this level were chosen. */
  root: ConnectorRootSchema.nullable()
})
export type BrowsePage = z.infer<typeof BrowsePageSchema>

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

/** Begin a sign-in. The browser is sent to the provider and comes back to the source. */
export const StartConnectorSignInRequestSchema = z
  .object({
    provider: z.enum(['microsoft', 'google']),
    sourceId: z.uuid(),
    /** Renew an existing connection rather than make a new one. */
    reconnectConnectionId: z.uuid().optional()
  })
  .strict()

/** Create a connection that needs no browser: S3 keys, a Google service account, Microsoft application mode. */
export const CreateConnectorConnectionRequestSchema = z.discriminatedUnion('provider', [
  z
    .object({
      provider: z.literal('s3'),
      accessKeyId: z.string().min(16).max(128),
      secretAccessKey: z.string().min(16).max(256),
      sessionToken: z.string().max(4096).optional(),
      region: z.string().min(1).max(32),
      /** An S3-compatible endpoint; must be one an operator admitted. */
      endpoint: z.url().optional(),
      forcePathStyle: z.boolean().optional(),
      /** A bucket to verify against, so a bad key fails here and not at the first sync. */
      bucket: z.string().min(3).max(63)
    })
    .strict(),
  z.object({ provider: z.literal('google'), serviceAccountKey: z.string().min(1).max(16_384) }).strict(),
  z.object({ provider: z.literal('microsoft'), tenantId: z.string().min(1).max(128) }).strict()
])

export const BrowseRequestSchema = z
  .object({
    surface: ConnectorSurfaceSchema,
    /** Provider-shaped position: absent for the top level. */
    at: z.record(z.string(), z.string()).optional(),
    next: z.string().optional(),
    /** SharePoint: the site search. */
    query: z.string().max(200).optional()
  })
  .strict()

export const CreateConnectorFolderRequestSchema = z
  .object({
    sourceId: z.uuid(),
    connectionId: z.uuid(),
    root: ConnectorRootSchema,
    displayPath: z.string().max(2048)
  })
  .strict()

/** One rule change: set an effect on a node, or clear it. */
export const SelectionRuleChangeSchema = z.object({
  remoteId: z.string().min(1),
  effect: z.enum(['include', 'exclude']).nullable()
})

export const UpdateSelectionRequestSchema = z
  .object({
    mode: SelectionModeSchema.optional(),
    filters: SelectionFiltersSchema.partial().optional(),
    rules: z.array(SelectionRuleChangeSchema).max(500).optional()
  })
  .strict()
export type UpdateSelectionRequest = z.infer<typeof UpdateSelectionRequestSchema>

/** What applying a selection change would do, computed on the server against the inventory. */
export const PendingChangesSchema = z.object({
  added: z.number().int().nonnegative(),
  addedBytes: z.number().int().nonnegative(),
  withdrawn: z.number().int().nonnegative(),
  sample: z.array(z.object({ name: z.string(), path: z.string(), change: z.enum(['added', 'withdrawn']) }))
})
export type PendingChanges = z.infer<typeof PendingChangesSchema>

/**
 * Start watching (D-8). The acknowledgement carries the sentence the person read, verbatim,
 * and is required whenever the source is open to everyone or the folder's acknowledgement is
 * stale; `ack` is a literal `true` so a client cannot satisfy it by omission.
 */
export const StartConnectorFolderRequestSchema = z
  .object({
    ack: z.literal(true).optional(),
    ackText: z.string().max(FOLDER_ACK_TEXT_MAX).optional(),
    /**
     * The language the sentence was read in (PRD-27 D-7). This flow persists only the locale:
     * `connector_folders` already records the acknowledgement structurally — who, when, which
     * access, which groups — so `0014` gave it the one thing it was missing rather than four
     * columns duplicating what the row already says.
     */
    ackLocale: LocaleSchema.optional()
  })
  .strict()

export const UpdateConnectorFolderRequestSchema = z
  .object({
    intervalMs: z.number().int().positive().optional(),
    /** Rebind a paused folder to another live connection of the same provider (D-10). */
    connectionId: z.uuid().optional()
  })
  .strict()

export const DisconnectFolderRequestSchema = z
  .object({
    /** Withdraw the documents it loaded; the default keeps them (D-17). */
    withdraw: z.boolean().default(false)
  })
  .strict()

export const RetryFailedRequestSchema = z.object({}).strict()

// ---------------------------------------------------------------------------
// The document's origin, as the knowledge DTOs carry it
// ---------------------------------------------------------------------------

export const DocumentOriginSchema = z.object({
  provider: ConnectorProviderSchema,
  surface: ConnectorSurfaceSchema.nullable(),
  path: z.string(),
  /** `https` only; rendered as a link, never fetched by the client. */
  url: z.string().nullable(),
  modifiedAt: z.iso.datetime().nullable(),
  /** Null once the folder is disconnected; the path and URL survive. */
  folderId: z.uuid().nullable(),
  /** The folder's entry for this file, while it is still connected; what "Fetch it again" acts on. */
  itemId: z.uuid().nullable(),
  accountLabel: z.string().nullable()
})
export type DocumentOrigin = z.infer<typeof DocumentOriginSchema>

/** Rolled up onto the source so the rail and the card can say when a folder needs a person. */
export const SourceFoldersSummarySchema = z.object({
  total: z.number().int().nonnegative(),
  active: z.number().int().nonnegative(),
  previewing: z.number().int().nonnegative(),
  needsPerson: z.number().int().nonnegative(),
  syncing: z.number().int().nonnegative()
})
export type SourceFoldersSummary = z.infer<typeof SourceFoldersSummarySchema>

export const PROVIDER_LABEL: Record<ConnectorProvider, string> = {
  microsoft: 'Microsoft',
  google: 'Google',
  s3: 'Amazon S3'
}

export const SURFACE_LABEL: Record<ConnectorSurface, string> = {
  sharepoint: 'SharePoint',
  onedrive: 'OneDrive',
  drive: 'Google Drive',
  s3: 'Amazon S3'
}

/** The named sync cadences a maintainer chooses among; never a number field. */
export const SYNC_INTERVALS = {
  soon: 30 * 60_000,
  fewTimesADay: 6 * 60 * 60_000,
  daily: 24 * 60 * 60_000
} as const
